import { NewsArticle } from '@/schema';

// Types for handling both old and new prediction formats
type OldPredictionFormat = {
  sentiment: 'neutru' | 'favorabil' | 'nefavorabil';
  scor: number;
  schimbare2lasuta: 'nustiu' | 'sus' | 'jos';
};

type NewPredictionFormat = {
  sentiment: 'positive' | 'neutral' | 'negative';
  confidence: number;
  symbol: string;
  rationale?: string;
  evidence?: string;
};

type PredictionJson = OldPredictionFormat | NewPredictionFormat;

// Get prediction data for a specific symbol
export const getPredictionDataForSymbol = (
  article?: NewsArticle,
  symbol?: string
) => {
  if (!article || !symbol) {
    return null;
  }

  // Find the symbol data for the specified symbol
  const symbolData = article.symbols?.find((s) => s.symbol === symbol);

  if (!symbolData || !symbolData.prediction_json) {
    return null;
  }

  const predictedDate = article.time_posted || article.created_at || '';

  const predictionJson = symbolData.prediction_json as PredictionJson;

  // Handle both old Romanian format and new English format
  let sentiment: string;
  let confidence: number | undefined;
  let changePercent: string | undefined;

  // Check if this is the new English format
  if ('confidence' in predictionJson) {
    // New format: { sentiment: 'positive' | 'neutral' | 'negative', confidence: float }
    const rawSentiment = predictionJson.sentiment;
    // Map English sentiment to Romanian
    sentiment =
      rawSentiment === 'positive'
        ? 'favorabil'
        : rawSentiment === 'negative'
          ? 'nefavorabil'
          : 'neutru';

    const rawConfidence = predictionJson.confidence;
    confidence =
      typeof rawConfidence === 'number' && !isNaN(rawConfidence)
        ? rawConfidence * 100 // confidence is 0-1, convert to percentage
        : undefined;
  } else {
    // Old Romanian format: { sentiment: 'neutru' | 'favorabil' | 'nefavorabil', scor: number }
    sentiment = predictionJson.sentiment;
    const scor = predictionJson.scor;
    confidence =
      typeof scor === 'number' && !isNaN(scor) ? scor * 10 : undefined;
    changePercent = predictionJson.schimbare2lasuta;
  }

  return {
    prediction: sentiment,
    confidence,
    predictedDate,
    changePercent,
  };
};

// Backward compatibility: Get prediction data for the first symbol (deprecated)
export const getPredictionData = (article?: NewsArticle) => {
  const firstSymbol = article?.symbols?.[0]?.symbol;
  return getPredictionDataForSymbol(article, firstSymbol);
};
